import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { MCP_BUILTIN_SERVER_KEY } from '@kentik/ui-shared/kaizen/mcpBuiltinServers';

import {
  Button,
  Callout,
  ConfirmDialog,
  Flex,
  FlexColumn,
  Heading,
  Icon,
  Select,
  Spinner,
  Switch,
  Text
} from 'core/components';
import { DEFAULT_DATETIME_FORMAT } from 'core/util/dateUtils';
import { showSuccessToast } from 'core/components/toast';
import { InfobloxLogo, InfobloxWordmark } from 'app/components/InfobloxLogo';
import { TeaserCard } from 'app/views/settings/ai/AIOverview';
import SettingsPanelHeading from 'app/views/settings/ai/SettingsPanelHeading';

const KEY = MCP_BUILTIN_SERVER_KEY.INFOBLOX_IQ;

const DISABLE_WARNING = 'Turning Infoblox off will expire authentication to Infoblox for all users';

const REALM_CHANGE_WARNING = 'Changing the Infoblox realm will expire authentication to Infoblox for all users';

// "US (csp.infoblox.com)": the host tells admins which Infoblox Portal their users sign in to.
const realmLabel = ({ name, url }) => {
  try {
    return `${name} (${new URL(url).host})`;
  } catch (err) {
    return name;
  }
};

const toolCountText = (count) => `${count} ${count === 1 ? 'tool' : 'tools'} available.`;

// pendingEnabled is the position being saved: the switch moves at once, and the spinner says the save is running.
const Banner = ({ enabled, pendingEnabled, canToggle, saveError, onToggle }) => (
  <TeaserCard style={{ minHeight: 'auto' }} fancyBorder>
    <Switch
      large
      checked={pendingEnabled ?? enabled}
      disabled={!canToggle || pendingEnabled !== null}
      onChange={onToggle}
      labelElement={
        <Flex flexDirection="column" alignItems="flex-start" gap="6px" ml={1}>
          <Heading level={5} m={0}>
            <InfobloxWordmark height={18} />
          </Heading>
          <Text muted fontWeight="medium">
            Allow AI Advisor to connect to your Infoblox account and include your DDI data in its reasoning.
          </Text>
        </Flex>
      }
    />
    {pendingEnabled !== null && (
      <Flex alignItems="center" gap={1} mt={1}>
        <Spinner size={14} />
        <Text small muted>
          {pendingEnabled ? 'Turning Infoblox on…' : 'Turning Infoblox off and signing out all users…'}
        </Text>
      </Flex>
    )}
    {!canToggle && (
      <Text small muted mt={1}>
        Only Administrators can turn Infoblox on or off.
      </Text>
    )}
    {saveError && (
      <Text small color="danger" mt={1}>
        {saveError}
      </Text>
    )}
  </TeaserCard>
);

const RealmSection = ({ serverState, pendingRealm, canChange, saveError, onChange }) => (
  <FlexColumn gap={1} alignItems="flex-start">
    <Heading level={4} m={0}>
      Infoblox realm
    </Heading>
    <Text muted>The Infoblox region AI Advisor connects to. Choose the realm your Infoblox account is hosted in.</Text>
    <Flex alignItems="center" gap={1}>
      <Select
        options={serverState.realms.map((realm) => ({ value: realm.key, label: realmLabel(realm) }))}
        values={pendingRealm ?? serverState.realm}
        disabled={!canChange || pendingRealm !== null}
        menuWidth={300}
        onChange={onChange}
      />
      {pendingRealm !== null && (
        <Flex alignItems="center" gap={1}>
          <Spinner size={14} />
          <Text small muted>
            Switching realm…
          </Text>
        </Flex>
      )}
    </Flex>
    {!canChange && (
      <Text small muted>
        Only Administrators can change the Infoblox realm.
      </Text>
    )}
    {saveError && (
      <Text small color="danger">
        {saveError}
      </Text>
    )}
  </FlexColumn>
);

// Shared across users, so an admin can tell whether the integration has ever worked for anyone.
const LastConnection = ({ serverState }) => {
  const at = serverState?.last_connected_at;

  return (
    <Flex alignItems="center" gap={1}>
      <Icon icon="full-circle" size={8} color={at ? 'success' : 'danger'} />
      <Text>
        Last user connection: <strong>{at ? `${moment.utc(at).format(DEFAULT_DATETIME_FORMAT)} UTC` : 'never'}</strong>{' '}
        by <strong>{at ? serverState.last_connected_by?.name || 'a deleted user' : 'no user'}</strong>
      </Text>
    </Flex>
  );
};

const AuthorizeLink = ({ authorizeUrl }) =>
  authorizeUrl ? (
    <Text small>
      Popup blocked.{' '}
      <a href={authorizeUrl} target="_blank" rel="noopener noreferrer">
        Open the Infoblox sign-in page
      </a>
    </Text>
  ) : null;

// Connected follows the user's stored grant; the tool count only exists once a test has listed the tools.
const ConnectionSection = ({ serverState, connection, userName, onConnect, onTest, onDisconnect }) => {
  const enabled = !!serverState?.enabled;
  const connected = enabled && (!!serverState?.connected || !!connection.result?.connected);
  const toolCount = connection.result?.tool_count;

  return (
    <FlexColumn gap={2} alignItems="flex-start">
      <Heading level={4} m={0}>
        Connection
      </Heading>
      <Flex alignItems="center" gap={1}>
        <InfobloxLogo size={28} />
        {connected ? (
          <Text>
            Connected to Infoblox as <strong>{userName}</strong>
            {toolCount !== undefined && ` · ${toolCount} ${toolCount === 1 ? 'tool' : 'tools'} available`}
          </Text>
        ) : (
          <Text>You&apos;re not connected to Infoblox</Text>
        )}
      </Flex>
      <LastConnection serverState={serverState} />
      {connection.error && (
        <Callout intent="danger">
          <Text>{connection.error}</Text>
        </Callout>
      )}
      <Flex gap={1} alignItems="center">
        {connected ? (
          <Button intent="primary" text="Test connection" loading={connection.testing} onClick={onTest} />
        ) : (
          <Button
            intent="primary"
            text="Connect to Infoblox"
            disabled={!enabled}
            loading={connection.testing}
            onClick={onConnect}
          />
        )}
        <Button
          intent="danger"
          text="Disconnect"
          loading={connection.disconnecting}
          disabled={!connected || connection.testing}
          onClick={onDisconnect}
        />
      </Flex>
      {!enabled && (
        <Text small muted>
          Ask an Administrator to turn on Infoblox before connecting.
        </Text>
      )}
      {enabled && !connected && (
        <Text small muted>
          Connect as {userName}. A new tab will open with the Infoblox Login to proceed with Authentication.
        </Text>
      )}
      <AuthorizeLink authorizeUrl={connection.authorizeUrl} />
    </FlexColumn>
  );
};

@inject('$auth', '$builtinMcpServers')
@observer
class InfobloxIqTab extends Component {
  state = {
    isConfirmOpen: false,
    pendingEnabled: null,
    saveError: null,
    confirmRealm: null,
    pendingRealm: null,
    realmError: null
  };

  componentDidMount() {
    const { $builtinMcpServers } = this.props;
    $builtinMcpServers.load(KEY);
  }

  setEnabled = (enabled) => {
    const { $builtinMcpServers } = this.props;

    this.setState({ pendingEnabled: enabled, saveError: null, isConfirmOpen: false });

    return $builtinMcpServers
      .setEnabled(KEY, enabled)
      .catch((err) => this.setState({ saveError: err.message || 'Could not save' }))
      .finally(() => this.setState({ pendingEnabled: null }));
  };

  handleToggle = (checked) => {
    if (checked) {
      this.setEnabled(true);
    } else {
      this.setState({ isConfirmOpen: true });
    }
  };

  setRealm = (realm) => {
    const { $builtinMcpServers } = this.props;

    this.setState({ pendingRealm: realm, realmError: null, confirmRealm: null });

    return $builtinMcpServers
      .setRealm(KEY, realm)
      .then((state) => {
        const chosen = state.realms.find((option) => option.key === state.realm);
        showSuccessToast(`Infoblox now connects to the ${chosen?.name ?? state.realm} realm.`);
      })
      .catch((err) => this.setState({ realmError: err.message || 'Could not change the realm' }))
      .finally(() => this.setState({ pendingRealm: null }));
  };

  // Users can only be signed in while the integration is on, so only then is there anyone to warn about.
  handleRealmChange = (realm) => {
    const { $builtinMcpServers } = this.props;
    const serverState = $builtinMcpServers.getState(KEY);

    if (realm === serverState.realm) {
      return;
    }

    if (serverState.enabled) {
      this.setState({ confirmRealm: realm });
    } else {
      this.setRealm(realm);
    }
  };

  // Failures already show inline; only success needs its own confirmation.
  handleTest = () => {
    const { $builtinMcpServers } = this.props;
    $builtinMcpServers.test(KEY).then((result) => {
      if (result?.connected) {
        showSuccessToast(`Infoblox connection is working. ${toolCountText(result.tool_count)}`);
      }
    });
  };

  handleConnect = () => {
    const { $builtinMcpServers } = this.props;
    $builtinMcpServers.connect(KEY).then((result) => {
      if (result?.connected) {
        showSuccessToast(`Connected to Infoblox. ${toolCountText(result.tool_count)}`);
      }
    });
  };

  handleDisconnect = () => {
    const { $builtinMcpServers } = this.props;
    $builtinMcpServers.disconnect(KEY);
  };

  render() {
    const { $auth, $builtinMcpServers } = this.props;
    const { isConfirmOpen, pendingEnabled, saveError, confirmRealm, pendingRealm, realmError } = this.state;
    const serverState = $builtinMcpServers.getState(KEY);
    const connection = $builtinMcpServers.getConnection(KEY);
    const userName = $auth.getActiveUserProperty('user_full_name');
    const loading = $builtinMcpServers.isLoading(KEY);
    const loadError = $builtinMcpServers.getLoadError(KEY);

    if (!serverState && loading) {
      return <Spinner mt={4} />;
    }

    return (
      <FlexColumn p={4}>
        <SettingsPanelHeading level={2} mb={2}>
          Infoblox
        </SettingsPanelHeading>
        <Text mb={3}>
          Each user signs in with their own Infoblox CSP account. AI Advisor can only see what that user is allowed to
          see in Infoblox.
        </Text>

        {loadError && (
          <Callout intent="danger" mb={3} maxWidth="800px">
            <Text>Could not load the Infoblox settings: {loadError}</Text>
            <Button small mt={1} text="Try again" loading={loading} onClick={() => $builtinMcpServers.load(KEY)} />
          </Callout>
        )}

        {/* Without a loaded state the switch would read as off, so it only shows once the state is known. */}
        {serverState && (
          <FlexColumn gap={4} maxWidth="800px">
            <Banner
              enabled={!!serverState?.enabled}
              canToggle={$auth.isAdministrator}
              pendingEnabled={pendingEnabled}
              saveError={saveError}
              onToggle={this.handleToggle}
            />

            <RealmSection
              serverState={serverState}
              pendingRealm={pendingRealm}
              canChange={$auth.isAdministrator}
              saveError={realmError}
              onChange={this.handleRealmChange}
            />

            <ConnectionSection
              serverState={serverState}
              connection={connection}
              userName={userName}
              onConnect={this.handleConnect}
              onTest={this.handleTest}
              onDisconnect={this.handleDisconnect}
            />
          </FlexColumn>
        )}

        <ConfirmDialog
          isOpen={isConfirmOpen}
          title="Turn off Infoblox?"
          text={DISABLE_WARNING}
          confirmButtonText="Confirm"
          confirmButtonIntent="danger"
          onCancel={() => this.setState({ isConfirmOpen: false })}
          onConfirm={() => this.setEnabled(false)}
        />

        <ConfirmDialog
          isOpen={confirmRealm !== null}
          title="Change the Infoblox realm?"
          text={REALM_CHANGE_WARNING}
          confirmButtonText="Confirm"
          confirmButtonIntent="danger"
          onCancel={() => this.setState({ confirmRealm: null })}
          onConfirm={() => this.setRealm(confirmRealm)}
        />
      </FlexColumn>
    );
  }
}

export default InfobloxIqTab;
